import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';

const OffboardingRequestStep = ({ 
  resignationDate,
  discussionStatus,
  discussionDate,
  notDiscussedComment,
  onChange,
  onFormSubmit,
  readOnly = false,
  userIdOverride
}) => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const effectiveUserId = userIdOverride || user?.id;

  // Fetch existing offboarding request data
  useEffect(() => {
    if (effectiveUserId) {
      axios.get(`${API_URL}/offboarding/offboarding-request/user/${effectiveUserId}`, {
        headers: getAuthHeaders()
      })
        .then(response => {
          const data = response.data;
          if (data) {
            // Populate form with existing data
            onChange({
              target: {
                name: 'resignationDate',
                value: data.resignationDate ? new Date(data.resignationDate).toISOString().split('T')[0] : ''
              }
            });
            onChange({
              target: {
                name: 'discussion.status',
                value: data.discussion?.status || 'Not Discussed'
              }
            });
            if (data.discussion?.discussionDate) {
              onChange({
                target: {
                  name: 'discussion.discussionDate',
                  value: new Date(data.discussion.discussionDate).toISOString().split('T')[0]
                }
              });
            }
            if (data.discussion?.notDiscussedComment) {
              onChange({
                target: {
                  name: 'discussion.notDiscussedComment',
                  value: data.discussion.notDiscussedComment
                }
              });
            }
            setIsUpdate(true);
          }
        })
        .catch(error => {
          if (error.response && error.response.status !== 404) {
            console.error('Error fetching offboarding request:', error);
            toast.error('Error loading existing request data');
          }
        });
    }
  }, [effectiveUserId, getAuthHeaders]);

  const handleSubmit = async () => {
    if (!resignationDate || !discussionStatus) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (discussionStatus === 'Discussed' && !discussionDate) {
      toast.error('Please provide discussion date');
      return;
    }

    if (discussionStatus === 'Not Discussed' && !notDiscussedComment) {
      toast.error('Please provide a reason for not discussing');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        resignationDate,
        discussion: {
          status: discussionStatus,
          discussionDate: discussionStatus === 'Discussed' ? discussionDate : null,
          notDiscussedComment: discussionStatus === 'Not Discussed' ? notDiscussedComment : null
        }
      };

      let response;
      if (isUpdate) {
        // Update existing offboarding request
        response = await axios.put(`${API_URL}/offboarding/offboarding-request/user/${effectiveUserId}`, requestData, {
          headers: getAuthHeaders()
        });
        toast.success('Offboarding request updated successfully!');
      } else {
        // Create new offboarding request
        response = await axios.post(`${API_URL}/offboarding/offboarding-request`, requestData, {
          headers: getAuthHeaders()
        });
        toast.success('Offboarding request submitted successfully!');
      }

      setIsUpdate(true);
      
      // Trigger validation update in parent component
      if (onFormSubmit) {
        onFormSubmit();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || `Failed to ${isUpdate ? 'update' : 'submit'} offboarding request`;
      toast.error(errorMessage);
      console.error(`Error ${isUpdate ? 'updating' : 'submitting'} offboarding request:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
  <Box>
    <Typography variant="subtitle1" sx={{ mb: 2 }}>
      Please fill in your offboarding request details.
    </Typography>
    <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Resignation Date"
          name="resignationDate"
          type="date"
          value={resignationDate}
          onChange={onChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Discussion Status</InputLabel>
          <Select
            name="discussion.status"
            value={discussionStatus}
            onChange={onChange}
            label="Discussion Status"
          >
            <MenuItem value="Not Discussed">Not Discussed</MenuItem>
            <MenuItem value="Discussed">Discussed</MenuItem>
          </Select>
        </FormControl>
      </Grid>
       {discussionStatus === 'Discussed' && (
         <Grid item xs={12} sm={6}>
           <TextField
             label="Discussion Date"
             name="discussion.discussionDate"
             type="date"
             value={discussionDate}
             onChange={onChange}
             InputLabelProps={{ shrink: true }}
             fullWidth
             required
           />
         </Grid>
       )}
       {discussionStatus === 'Not Discussed' && (
         <Grid item xs={12}>
           <TextField
             label="Reason"
             name="discussion.notDiscussedComment"
             value={notDiscussedComment}
             onChange={onChange}
             fullWidth
             multiline
             rows={3}
             placeholder="Please provide a reason why the discussion was not held..."
             required
           />
         </Grid>
       )}
    </Grid>
    </fieldset>
    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
      {!readOnly && (
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            isUpdate ? 'Update Form' : 'Submit Form'
          )}
        </Button>
      )}
    </Box>
  </Box>
  );
};

export default OffboardingRequestStep; 